import * as React from 'react';

/**
 * Gap value type - maps to spacing scale
 * 0 = 0, 1 = 4px, 2 = 8px, 3 = 12px, 4 = 16px, 5 = 20px, 6 = 24px, 8 = 32px
 */
export type GridGap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8;

/**
 * Alignment options (align-items)
 */
export type GridAlign = 'start' | 'center' | 'end' | 'stretch';

/**
 * Justification options (justify-items)
 */
export type GridJustify = 'start' | 'center' | 'end' | 'stretch';

/**
 * Grid auto-flow options
 */
export type GridFlow = 'row' | 'column' | 'dense' | 'row-dense' | 'column-dense';

/**
 * Responsive column configuration
 */
export interface ResponsiveColumns {
  base?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  '2xl'?: number;
}

/**
 * Responsive span configuration
 */
export interface ResponsiveSpan {
  base?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  '2xl'?: number;
}

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Number of columns or responsive column configuration
   * @default 12
   */
  columns?: number | ResponsiveColumns;
  /**
   * Gap between items (maps to spacing scale)
   * 0 = 0, 1 = 4px, 2 = 8px, 3 = 12px, 4 = 16px, 5 = 20px, 6 = 24px, 8 = 32px
   * @default 6
   */
  gap?: GridGap;
  /**
   * Row gap (overrides gap for rows)
   */
  rowGap?: GridGap;
  /**
   * Column gap (overrides gap for columns)
   */
  columnGap?: GridGap;
  /**
   * Alignment of items on the block axis
   * @default 'stretch'
   */
  align?: GridAlign;
  /**
   * Justification of items on the inline axis
   * @default 'stretch'
   */
  justify?: GridJustify;
  /**
   * Grid auto-flow behavior
   */
  flow?: GridFlow;
  /**
   * Grid children
   */
  children?: React.ReactNode;
}

export interface GridItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Number of columns to span or responsive span configuration
   * @default 1
   */
  span?: number | ResponsiveSpan;
  /**
   * Grid column start position
   */
  start?: number;
  /**
   * Grid column end position
   */
  end?: number;
  /**
   * Number of rows to span
   */
  rowSpan?: number;
  /**
   * Grid row start position
   */
  rowStart?: number;
  /**
   * Grid row end position
   */
  rowEnd?: number;
  /**
   * GridItem children
   */
  children?: React.ReactNode;
}

/**
 * Gap classes mapping to Tailwind gap utilities
 */
const gapClasses: Record<GridGap, string> = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
};

/**
 * Row gap classes mapping
 */
const rowGapClasses: Record<GridGap, string> = {
  0: 'gap-y-0',
  1: 'gap-y-1',
  2: 'gap-y-2',
  3: 'gap-y-3',
  4: 'gap-y-4',
  5: 'gap-y-5',
  6: 'gap-y-6',
  8: 'gap-y-8',
};

/**
 * Column gap classes mapping
 */
const columnGapClasses: Record<GridGap, string> = {
  0: 'gap-x-0',
  1: 'gap-x-1',
  2: 'gap-x-2',
  3: 'gap-x-3',
  4: 'gap-x-4',
  5: 'gap-x-5',
  6: 'gap-x-6',
  8: 'gap-x-8',
};

/**
 * Alignment classes mapping
 */
const alignClasses: Record<GridAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

/**
 * Justification classes mapping
 */
const justifyClasses: Record<GridJustify, string> = {
  start: 'justify-items-start',
  center: 'justify-items-center',
  end: 'justify-items-end',
  stretch: 'justify-items-stretch',
};

/**
 * Flow classes mapping
 */
const flowClasses: Record<GridFlow, string> = {
  row: 'grid-flow-row',
  column: 'grid-flow-col',
  dense: 'grid-flow-dense',
  'row-dense': 'grid-flow-row-dense',
  'column-dense': 'grid-flow-col-dense',
};

/**
 * Grid column classes for different column counts
 */
const gridColsClasses: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  7: 'grid-cols-7',
  8: 'grid-cols-8',
  9: 'grid-cols-9',
  10: 'grid-cols-10',
  11: 'grid-cols-11',
  12: 'grid-cols-12',
};

/**
 * Responsive grid column classes
 */
const responsiveGridColsClasses: Record<string, Record<number, string>> = {
  sm: {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5',
    6: 'sm:grid-cols-6',
    7: 'sm:grid-cols-7',
    8: 'sm:grid-cols-8',
    9: 'sm:grid-cols-9',
    10: 'sm:grid-cols-10',
    11: 'sm:grid-cols-11',
    12: 'sm:grid-cols-12',
  },
  md: {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
    5: 'md:grid-cols-5',
    6: 'md:grid-cols-6',
    7: 'md:grid-cols-7',
    8: 'md:grid-cols-8',
    9: 'md:grid-cols-9',
    10: 'md:grid-cols-10',
    11: 'md:grid-cols-11',
    12: 'md:grid-cols-12',
  },
  lg: {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
    6: 'lg:grid-cols-6',
    7: 'lg:grid-cols-7',
    8: 'lg:grid-cols-8',
    9: 'lg:grid-cols-9',
    10: 'lg:grid-cols-10',
    11: 'lg:grid-cols-11',
    12: 'lg:grid-cols-12',
  },
  xl: {
    1: 'xl:grid-cols-1',
    2: 'xl:grid-cols-2',
    3: 'xl:grid-cols-3',
    4: 'xl:grid-cols-4',
    5: 'xl:grid-cols-5',
    6: 'xl:grid-cols-6',
    7: 'xl:grid-cols-7',
    8: 'xl:grid-cols-8',
    9: 'xl:grid-cols-9',
    10: 'xl:grid-cols-10',
    11: 'xl:grid-cols-11',
    12: 'xl:grid-cols-12',
  },
  '2xl': {
    1: '2xl:grid-cols-1',
    2: '2xl:grid-cols-2',
    3: '2xl:grid-cols-3',
    4: '2xl:grid-cols-4',
    5: '2xl:grid-cols-5',
    6: '2xl:grid-cols-6',
    7: '2xl:grid-cols-7',
    8: '2xl:grid-cols-8',
    9: '2xl:grid-cols-9',
    10: '2xl:grid-cols-10',
    11: '2xl:grid-cols-11',
    12: '2xl:grid-cols-12',
  },
};

/**
 * Column span classes
 */
const colSpanClasses: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};

/**
 * Responsive column span classes
 */
const responsiveColSpanClasses: Record<string, Record<number, string>> = {
  sm: {
    1: 'sm:col-span-1',
    2: 'sm:col-span-2',
    3: 'sm:col-span-3',
    4: 'sm:col-span-4',
    5: 'sm:col-span-5',
    6: 'sm:col-span-6',
    7: 'sm:col-span-7',
    8: 'sm:col-span-8',
    9: 'sm:col-span-9',
    10: 'sm:col-span-10',
    11: 'sm:col-span-11',
    12: 'sm:col-span-12',
  },
  md: {
    1: 'md:col-span-1',
    2: 'md:col-span-2',
    3: 'md:col-span-3',
    4: 'md:col-span-4',
    5: 'md:col-span-5',
    6: 'md:col-span-6',
    7: 'md:col-span-7',
    8: 'md:col-span-8',
    9: 'md:col-span-9',
    10: 'md:col-span-10',
    11: 'md:col-span-11',
    12: 'md:col-span-12',
  },
  lg: {
    1: 'lg:col-span-1',
    2: 'lg:col-span-2',
    3: 'lg:col-span-3',
    4: 'lg:col-span-4',
    5: 'lg:col-span-5',
    6: 'lg:col-span-6',
    7: 'lg:col-span-7',
    8: 'lg:col-span-8',
    9: 'lg:col-span-9',
    10: 'lg:col-span-10',
    11: 'lg:col-span-11',
    12: 'lg:col-span-12',
  },
  xl: {
    1: 'xl:col-span-1',
    2: 'xl:col-span-2',
    3: 'xl:col-span-3',
    4: 'xl:col-span-4',
    5: 'xl:col-span-5',
    6: 'xl:col-span-6',
    7: 'xl:col-span-7',
    8: 'xl:col-span-8',
    9: 'xl:col-span-9',
    10: 'xl:col-span-10',
    11: 'xl:col-span-11',
    12: 'xl:col-span-12',
  },
  '2xl': {
    1: '2xl:col-span-1',
    2: '2xl:col-span-2',
    3: '2xl:col-span-3',
    4: '2xl:col-span-4',
    5: '2xl:col-span-5',
    6: '2xl:col-span-6',
    7: '2xl:col-span-7',
    8: '2xl:col-span-8',
    9: '2xl:col-span-9',
    10: '2xl:col-span-10',
    11: '2xl:col-span-11',
    12: '2xl:col-span-12',
  },
};

/**
 * Column start classes
 */
const colStartClasses: Record<number, string> = {
  1: 'col-start-1',
  2: 'col-start-2',
  3: 'col-start-3',
  4: 'col-start-4',
  5: 'col-start-5',
  6: 'col-start-6',
  7: 'col-start-7',
  8: 'col-start-8',
  9: 'col-start-9',
  10: 'col-start-10',
  11: 'col-start-11',
  12: 'col-start-12',
  13: 'col-start-13',
};

/**
 * Column end classes
 */
const colEndClasses: Record<number, string> = {
  1: 'col-end-1',
  2: 'col-end-2',
  3: 'col-end-3',
  4: 'col-end-4',
  5: 'col-end-5',
  6: 'col-end-6',
  7: 'col-end-7',
  8: 'col-end-8',
  9: 'col-end-9',
  10: 'col-end-10',
  11: 'col-end-11',
  12: 'col-end-12',
  13: 'col-end-13',
};

/**
 * Row span classes
 */
const rowSpanClasses: Record<number, string> = {
  1: 'row-span-1',
  2: 'row-span-2',
  3: 'row-span-3',
  4: 'row-span-4',
  5: 'row-span-5',
  6: 'row-span-6',
};

/**
 * Row start classes
 */
const rowStartClasses: Record<number, string> = {
  1: 'row-start-1',
  2: 'row-start-2',
  3: 'row-start-3',
  4: 'row-start-4',
  5: 'row-start-5',
  6: 'row-start-6',
  7: 'row-start-7',
};

/**
 * Row end classes
 */
const rowEndClasses: Record<number, string> = {
  1: 'row-end-1',
  2: 'row-end-2',
  3: 'row-end-3',
  4: 'row-end-4',
  5: 'row-end-5',
  6: 'row-end-6',
  7: 'row-end-7',
};

/**
 * Join a list of class name fragments, filtering out falsy values.
 */
function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Get column classes from columns prop
 */
function getColumnClasses(columns: number | ResponsiveColumns): string {
  if (typeof columns === 'number') {
    return gridColsClasses[columns] || '';
  }

  const classes: string[] = [];

  if (columns.base !== undefined) {
    const baseClass = gridColsClasses[columns.base];
    if (baseClass) {
      classes.push(baseClass);
    }
  }

  const breakpoints = ['sm', 'md', 'lg', 'xl', '2xl'] as const;
  for (const bp of breakpoints) {
    const colCount = columns[bp];
    if (colCount !== undefined) {
      const bpClasses = responsiveGridColsClasses[bp];
      const cls = bpClasses?.[colCount];
      if (cls) {
        classes.push(cls);
      }
    }
  }

  return classes.join(' ');
}

/**
 * Get span classes from span prop
 */
function getSpanClasses(span: number | ResponsiveSpan): string {
  if (typeof span === 'number') {
    return colSpanClasses[span] || '';
  }

  const classes: string[] = [];

  if (span.base !== undefined) {
    const baseClass = colSpanClasses[span.base];
    if (baseClass) {
      classes.push(baseClass);
    }
  }

  const breakpoints = ['sm', 'md', 'lg', 'xl', '2xl'] as const;
  for (const bp of breakpoints) {
    const spanCount = span[bp];
    if (spanCount !== undefined) {
      const bpClasses = responsiveColSpanClasses[bp];
      const cls = bpClasses?.[spanCount];
      if (cls) {
        classes.push(cls);
      }
    }
  }

  return classes.join(' ');
}

/**
 * Grid component
 *
 * A flexible CSS Grid-based layout component for creating grid layouts.
 * Supports 12-column grids with responsive columns and gaps.
 *
 * @example
 * // Basic 12-column grid
 * <Grid columns={12} gap={6}>
 *   <Grid.Item span={6}>Half width</Grid.Item>
 *   <Grid.Item span={6}>Half width</Grid.Item>
 * </Grid>
 *
 * @example
 * // Responsive grid
 * <Grid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
 *   <Grid.Item>Item 1</Grid.Item>
 *   <Grid.Item>Item 2</Grid.Item>
 *   <Grid.Item>Item 3</Grid.Item>
 *   <Grid.Item>Item 4</Grid.Item>
 * </Grid>
 *
 * @example
 * // Grid with positioned items
 * <Grid columns={12} gap={4}>
 *   <Grid.Item span={8} start={3}>Centered content</Grid.Item>
 * </Grid>
 */
const GridRoot = React.forwardRef<HTMLDivElement, GridProps>(
  (
    {
      columns = 12,
      gap = 6,
      rowGap,
      columnGap,
      align = 'stretch',
      justify = 'stretch',
      flow,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const gapClass = rowGap !== undefined || columnGap !== undefined
      ? cx(
          rowGap !== undefined && rowGapClasses[rowGap],
          columnGap !== undefined && columnGapClasses[columnGap],
        )
      : gapClasses[gap];

    const combinedClasses = cx(
      'grid',
      getColumnClasses(columns),
      gapClass,
      alignClasses[align],
      justifyClasses[justify],
      flow ? flowClasses[flow] : '',
      className,
    );

    return (
      <div ref={ref} className={combinedClasses} {...props}>
        {children}
      </div>
    );
  }
);

GridRoot.displayName = 'Grid';

/**
 * GridItem component
 *
 * A child component for Grid that controls its placement within the grid.
 *
 * @example
 * // Basic span
 * <Grid.Item span={6}>Half width</Grid.Item>
 *
 * @example
 * // Responsive span
 * <Grid.Item span={{ base: 12, md: 6, lg: 4 }}>
 *   Full on mobile, half on tablet, third on desktop
 * </Grid.Item>
 *
 * @example
 * // Positioned item
 * <Grid.Item start={2} end={5}>
 *   Starts at column 2, ends before column 5
 * </Grid.Item>
 */
const GridItem = React.forwardRef<HTMLDivElement, GridItemProps>(
  (
    {
      span = 1,
      start,
      end,
      rowSpan,
      rowStart,
      rowEnd,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const combinedClasses = cx(
      getSpanClasses(span),
      start !== undefined && colStartClasses[start],
      end !== undefined && colEndClasses[end],
      rowSpan !== undefined && rowSpanClasses[rowSpan],
      rowStart !== undefined && rowStartClasses[rowStart],
      rowEnd !== undefined && rowEndClasses[rowEnd],
      className,
    );

    return (
      <div ref={ref} className={combinedClasses} {...props}>
        {children}
      </div>
    );
  }
);

GridItem.displayName = 'Grid.Item';

/**
 * Grid compound component
 */
const Grid = Object.assign(GridRoot, {
  Item: GridItem,
});

export { Grid, GridItem };
